import * as yup from 'yup';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { DocumentNode } from 'graphql';
import { X } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  CouponType,
  GetPurchaseDetailsQuery as GetPurchaseDetails,
  GetTicketsAndProductsForPurchaseQuery as GetTicketsAndProductsForPurchase,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditPurchaseFromBackendMutation from './EditPurchaseFromBackendMutation';
import GetPurchaseDetailsQuery from './GetPurchaseDetailsQuery';
import GetTicketsAndProductsForPurchaseQuery from './GetTicketsAndProductsForPurchaseQuery';
import PurchaseInfo from './PurchaseInfo';
import RegistrationInfo from '../About/RegistrationInfo';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useTimeSlotFormatter from '../../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../../common/useValidation';

type Event = GetTicketsAndProductsForPurchase['event'];
type Ticket = Event['tickets'][0];
type Product = Event['products'][0];
type Promotion = Ticket['promotions'][0];

type Purchase = GetPurchaseDetails['project']['purchase'];

export interface EditPurchaseFormProps {
  purchase: Purchase;
  event: Event;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditPurchaseForm = ({
  purchase,
  event,
  onSuccess,
  onCancel,
  refetchQueries = [],
}: EditPurchaseFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatInteger } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();
  const currencyInput = useCurrencyInput();
  const notifier = useNotifier();

  const tickets = event.tickets.filter((ticket) => (
    ticket.units === purchase.ticket?.units
  )) || [];
  const products = event.products || [];
  const coupons = event.coupons || [];
  const invitations = event.invitations || [];

  const getValues = (purchase: Purchase) => ({
    amount: purchase.amount,
    ticket: purchase.ticket ? { id: purchase.ticket.id } : null,
    product: purchase.product ? { id: purchase.product.id } : null,
    promotion: purchase.promotion ? { id: purchase.promotion.id } : null,
    product_variant: purchase.product_variant ? { id: purchase.product_variant.id } : null,
    time_slot: purchase.time_slot ? { id: purchase.time_slot.id } : null,
    coupon: purchase.coupon_code?.value,
    invitation_code: purchase.invitation_code?.value,
  });

  const form = useForm({
    values: getValues(purchase),
  });

  const ticket = tickets.filter((ticket) => ticket.id === form.values.ticket?.id)[0];
  const product = products.filter((product) => product.id === form.values.product?.id)[0];
  const promotions = ticket?.promotions || product?.promotions || [];
  const timeSlots = ticket?.time_slots || [];
  const productVariants = product?.product_variants || [];

  const amountRef = useRef<HTMLInputElement>();

  const setAmount = (amount: number) => {
    form.set('amount', amount);
    amountRef.current.value = currencyInput.value(amount);
  };

  const handleTicketChange = (ticket: Ticket) => {
    // Select the promotion that was originally selected when the original ticket is selected,
    // otherwise simply select the first promotion
    const promotion = ticket.id === purchase.ticket.id
      ? purchase.promotion : ticket.promotions.filter((promotion) => promotion.standard)[0];

    // Select the time slot that was originally selected when the original product is selected,
    // otherwise simply select the first time slot
    const timeSlot = (ticket.id === purchase.ticket.id && purchase.time_slot) || ticket.time_slots?.[0];

    const amount = Math.min(purchase.revenue, promotion.amount);

    form.set((values) => ({
      ...values,
      ticket: { id: ticket.id },
      promotion: { id: promotion.id },
      time_slot: timeSlot ? { id: timeSlot.id } : null,
      amount,
    }));

    amountRef.current.value = currencyInput.value(amount);
  };

  const handleProductChange = (product: Product) => {
    // Select the promotion that was originally selected when the original product is selected,
    // otherwise simply select the first promotion
    const promotion = product.id === purchase.product.id
      ? purchase.promotion : product.promotions.filter((promotion) => promotion.standard)[0];

    // Select the product variant that was originally selected when the original product is selected,
    // otherwise simply select the first product variant
    const productVariant = (product.id === purchase.product.id && purchase.product_variant)
      || product.product_variants?.[0];

    const amount = Math.min(purchase.revenue, promotion.amount);

    form.set((values) => ({
      ...values,
      product: { id: product.id },
      promotion: { id: promotion.id },
      product_variant: productVariant ? { id: productVariant.id } : null,
      amount,
    }));

    amountRef.current.value = currencyInput.value(amount);
  };

  const handlePromotionChange = (promotion: Promotion) => {
    form.set('promotion', { id: promotion.id });
    setAmount(Math.min(purchase.revenue, promotion.amount));
  };

  const couponRef = useRef<HTMLInputElement>();

  const [
    selectedCoupon, setSelectedCoupon,
  ] = useState(coupons.filter(({ id }) => id === purchase.coupon_code?.coupon.id)[0]);

  const handleCouponChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const newSelectedCoupon = coupons[parseInt(changeEvent.target.value, 10)];
    setSelectedCoupon(newSelectedCoupon);
    form.set('coupon', newSelectedCoupon?.prefix);

    // Focus the coupon code input after the next render
    window.setTimeout(() => {
      if (couponRef.current) {
        couponRef.current.focus();
      }
    }, 0);
  };

  const clearCoupon = () => {
    form.set('coupon', null);
    setSelectedCoupon(null);
  };

  const invitationCodeRef = useRef<HTMLInputElement>();

  const [
    selectedInvitation, setSelectedInvitation,
  ] = useState(invitations.filter(({ id }) => id === purchase.invitation_code?.invitation.id)[0]);

  const handleInvitationChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const newSelectedInvitation = invitations[parseInt(changeEvent.target.value, 10)];
    setSelectedInvitation(newSelectedInvitation);
    form.set('invitation_code', newSelectedInvitation?.prefix);

    // Focus the invitation code input after the next render
    window.setTimeout(() => {
      if (invitationCodeRef.current) {
        invitationCodeRef.current.focus();
      }
    }, 0);
  };

  const clearInvitation = () => {
    form.set('invitation_code', null);
    setSelectedInvitation(null);
  };

  const [editPurchase, { error, loading }] = useMutation(
    EditPurchaseFromBackendMutation,
    {
      variables: {
        input: {
          id: purchase.id,
          ...form.values,
          delete_coupon: purchase.coupon_code ? !form.values.coupon : false,
          delete_invitation_code: purchase.invitation_code ? !form.values.invitation_code : false,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onSuccess?.();

        notifier.success(t('notifications.purchase_edited'));
      },
    },
  );

  const amountField = yup.number().required();
  const maxAmount = !purchase.active ? purchase.revenue : null;

  const { valid, errors } = useValidation({
    schema: yup.object({
      amount: maxAmount === null ? amountField : (amountField as any).maxAmount(maxAmount),
    }),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const creditChange = form.values.amount !== null
    ? Math.max(purchase.revenue - form.values.amount, 0) - purchase.balance
    : 0;

  return (
    <UI.Form onSubmit={() => editPurchase()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('edit_purchase')}
          </UI.H4>
        </UI.Div>

        {purchase.registration && purchase.ticket ? (
          <RegistrationInfo
            registration={{
              ...purchase.registration,
              ticket: purchase.ticket,
              participant: purchase.participant,
              purchase,
            }}
          />
        ) : (
          <PurchaseInfo purchase={purchase} />
        )}

        <UI.ServerErrors error={error} />

        <UI.FormGrid sc={{ columns: ['1fr', productVariants.length + timeSlots.length > 0 ? '1fr 1fr' : '1fr'] }}>
          {purchase.ticket && (
            <UI.InputGroup sc={{ valid: !errors?.ticket, touched: form.getTouched('ticket') }}>
              <UI.InputLabel htmlFor="Ticket">
                {t('ticket')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.Select
                {...form.select('ticket', tickets, { key: 'id', onChange: handleTicketChange })}
                id="Ticket"
              >
                {tickets.map((ticket, index) => (
                  <option {...form.option(index)} key={index}>
                    {ticket.title}
                    {' • '}
                    {formatCurrency(ticket.promotions.filter((promotion) => promotion.standard)[0].amount)}
                    {ticket.is_sold_out && ` • ${t('sold_out')}`}
                  </option>
                ))}
              </UI.Select>
              <UI.ErrorMessages attribute={t('ticket')} errors={errors.ticket} />
            </UI.InputGroup>
          )}

          {purchase.product && (
            <UI.InputGroup sc={{ valid: !errors?.product, touched: form.getTouched('product') }}>
              <UI.InputLabel htmlFor="Product">
                {t('product')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.Select
                {...form.select('product', products, { key: 'id', onChange: handleProductChange })}
                id="Product"
              >
                {products.map((product, index) => (
                  <option {...form.option(index)} key={index}>
                    {product.title}
                    {' • '}
                    {formatCurrency(product.promotions.filter((promotion) => promotion.standard)[0].amount)}
                    {product.is_sold_out && ` • ${t('sold_out')}`}
                  </option>
                ))}
              </UI.Select>
              <UI.ErrorMessages attribute={t('product')} errors={errors.product} />
            </UI.InputGroup>
          )}

          {timeSlots.length > 0 && (
            <UI.InputGroup sc={{ valid: !errors?.time_slot, touched: form.getTouched('time_slot') }}>
              <UI.InputLabel htmlFor="TimeSlot">
                {t('time_slot')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.Select
                {...form.select('time_slot', timeSlots.map(({ id }) => ({ id })), { key: 'id' })}
                id="TimeSlot"
              >
                {!form.values.time_slot && (
                  <option value={-1}> </option>
                )}
                {timeSlots.map((timeSlot, index) => (
                  <option {...form.option(index)} key={index}>
                    {formatTimeSlot(timeSlot)}
                    {timeSlot.title && ` • ${timeSlot.title}`}
                    {timeSlot.is_sold_out && ` • ${t('full')}`}
                  </option>
                ))}
              </UI.Select>
              <UI.ErrorMessages attribute={t('time_slot')} errors={errors.time_slot} />
            </UI.InputGroup>
          )}

          {productVariants.length > 1 && (
            <UI.InputGroup sc={{ valid: !errors?.product_variant, touched: form.getTouched('product_variant') }}>
              <UI.InputLabel htmlFor="ProductVariant">
                {t('product_variant')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.Select
                {...form.select('product_variant', productVariants.map(({ id }) => ({ id })), { key: 'id' })}
                id="ProductVariant"
              >
                {!form.values.product_variant && (
                  <option value={-1}> </option>
                )}
                {productVariants.map((productVariant, index) => (
                  <option {...form.option(index)} key={index}>
                    {productVariant.title}
                    {productVariant.is_sold_out && ` • ${t('sold_out')}`}
                  </option>
                ))}
              </UI.Select>
              <UI.ErrorMessages attribute={t('product_variant')} errors={errors.product_variant} />
            </UI.InputGroup>
          )}
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors?.promotion, touched: form.getTouched('promotion') }}>
            <UI.InputLabel htmlFor="Promotion">
              {t('promotion')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.Select
              {...form.select('promotion', promotions, { key: 'id', onChange: handlePromotionChange })}
              id="Promotion"
            >
              {promotions.map((promotion, index) => (
                <option {...form.option(index)} key={index}>
                  {promotion.title || t('standard')}
                  {' • '}
                  {formatCurrency(promotion.amount)}
                  {promotion.is_sold_out && ` • ${t('sold_out')}`}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('promotion')} errors={errors.promotion} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors?.amount, touched: form.getTouched('amount') }}>
            <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <UI.InputLabel htmlFor="Amount">
                {t('amount')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.A
                onClick={() => setAmount(purchase.revenue)}
                sc={{
                  secondary: true,
                  muted: form.values.amount === purchase.revenue,
                  mt: 0,
                }}
              >
                {`${t('paid')}: ${formatCurrency(purchase.revenue)}`}
              </UI.A>
            </UI.FlexContainer>

            <UI.CurrencyInput
              {...form.text('amount', {
                inputHandler: {
                  parse: currencyInput.parse,
                  format: currencyInput.value,
                },
              })}
              ref={amountRef}
              id="Amount"
            />
            <UI.ErrorMessages attribute={t('amount')} errors={errors.amount} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          {coupons.length > 0 && (
            <UI.InputGroup sc={{ valid: !errors?.coupon, touched: form.getTouched('coupon') }}>
              <UI.InputLabel htmlFor="Coupon">
                {t('coupon_code')}
              </UI.InputLabel>
              {(!selectedCoupon || selectedCoupon.codes_count === 0) && (
                <UI.Select
                  value={
                    selectedCoupon
                      ? coupons.findIndex(({ id }) => id === selectedCoupon?.id)
                      : -1
                  }
                  onChange={handleCouponChange}
                  id="Coupon"
                >
                  <option value={-1}> </option>
                  {coupons.map((coupon, index) => (
                    <option value={index} key={coupon.id}>
                      {coupon.code.value}
                      {' • '}
                      {coupon.type === CouponType.absolute && formatCurrency(-coupon.value)}
                      {coupon.type === CouponType.percentage && `${formatInteger(-coupon.value)}%`}
                      {!coupon.available && ` • ${t('used_up')}`}
                    </option>
                  ))}
                </UI.Select>
              )}

              {selectedCoupon?.codes_count > 0 && (
                <UI.InputWrapper>
                  <UI.DebouncedInput
                    {...form.text('coupon')}
                    placeholder={`${selectedCoupon.prefix}...`}
                    id="Coupon"
                    ref={couponRef}
                    sc={{ pr: 5 }}
                  />
                  <UI.Span>
                    <UI.A onClick={() => clearCoupon()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                </UI.InputWrapper>
              )}

              <UI.ErrorMessages attribute={t('coupon')} errors={errors.coupon} />
            </UI.InputGroup>
          )}
          {ticket && invitations.length > 0 && (
            <UI.InputGroup sc={{ valid: !errors?.invitation, touched: form.getTouched('invitation') }}>
              <UI.InputLabel htmlFor="Invitation">
                {t('invitation_code')}
              </UI.InputLabel>
              {(!selectedInvitation || !selectedInvitation.has_unique_codes) && (
                <UI.Select
                  value={
                    selectedInvitation
                      ? invitations.findIndex(({ id }) => id === selectedInvitation?.id)
                      : -1
                  }
                  onChange={handleInvitationChange}
                  id="Invitation"
                >
                  <option value={-1}> </option>
                  {invitations.map((invitation, index) => (
                    <option value={index} key={invitation.id}>
                      {invitation.code.value}
                      {!invitation.available && ` • ${t('used_up')}`}
                    </option>
                  ))}
                </UI.Select>
              )}

              {selectedInvitation?.has_unique_codes && (
                <UI.InputWrapper>
                  <UI.DebouncedInput
                    {...form.text('invitation_code')}
                    placeholder={`${selectedInvitation.prefix}...`}
                    id="Invitation"
                    ref={invitationCodeRef}
                    sc={{ pr: 5 }}
                  />
                  <UI.Span>
                    <UI.A onClick={() => clearInvitation()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                </UI.InputWrapper>
              )}

              <UI.ErrorMessages attribute={t('invitation_code')} errors={errors.invitation} />
            </UI.InputGroup>
          )}
        </UI.FormGrid>

        {creditChange > 0 && (
          <UI.Info>
            <UI.Strong sc={{ block: true }}>
              {`${t('gained_credit')}: + ${formatCurrency(creditChange)}`}
            </UI.Strong>
            {t('gained_credit_description')}
          </UI.Info>
        )}

        <UI.HR sc={{ my: 0 }} />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditPurchaseFormProps {
  purchaseId: string;
  eventId: string;
  open?: boolean;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const useEditPurchaseModal = ({
  purchaseId,
  eventId,
  open = false,
  refetchQueries,
}: UseEditPurchaseFormProps) => {
  const [openModal, closeModal] = useModal(() => {
    const project = useProject();

    const handleSuccess = () => {
      closeModal();
    };

    const { data: purchaseData } = useQuery(
      GetPurchaseDetailsQuery,
      {
        variables: {
          project_id: project.id,
          purchase_id: purchaseId,
        },
        fetchPolicy: 'no-cache',
      },
    );

    const {
      data: eventData,
    } = useQuery(
      GetTicketsAndProductsForPurchaseQuery,
      {
        variables: {
          event_id: eventId,
        },
      },
    );

    const purchase = purchaseData?.project.purchase;
    const event = eventData?.event;

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {(!purchase || !event) && <UI.PageLoader />}

        {purchase && event && (
          <EditPurchaseForm
            purchase={purchase}
            event={event}
            onSuccess={handleSuccess}
            onCancel={closeModal}
            refetchQueries={refetchQueries}
          />
        )}
      </UI.Modal>
    );
  }, [purchaseId, refetchQueries]);

  useEffect(() => {
    if (open) {
      openModal();
    } else {
      closeModal();
    }
  }, [open]);

  return [openModal, closeModal];
};

export default useEditPurchaseModal;
